import React, { useState, useMemo, useEffect } from 'react';
import FormField from './FormField';
import AddressLookup from './AddressLookup';
import CTAButton from './CTAButton';
import { useVerifoneCheckout } from '../hooks/useVerifoneCheckout';

import { ReactComponent as SafePayment } from '../static/img/safe-payment.svg';

import './ConsultantCardholderDetails.sass';

export default ({
  cardholderForm,
  patientForm,
  hospitalId,
  onChange,
  setPaymentToken,
  setCheckoutRendered,
  setPaymentStatus,
  paymentStatus,
  setError,
  cardAttempts,
  setCardAttempts,
  setCheckoutId,
  setCheckoutBilling,
}) => {
  const [cardholderSelection, setCardholderSelection] = useState(null);
  const [isDifferentCardholderConfirmed, setIsDifferentCardholderConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useVerifoneCheckout({
    cardholderSelection,
    isDifferentCardholderConfirmed,
    hospitalId,
    cardholderForm,
    patientForm,
    setCheckoutRendered,
    setPaymentStatus,
    setLoading,
    setError,
    cardAttempts,
    setCardAttempts,
    setCheckoutId,
    setCheckoutBilling,
  });

  const isCardholderDetailsValid = () => {
    if (cardholderSelection !== 'different') {
      return true;
    }

    return (
      cardholderForm.cardholder_first_name.value?.trim() &&
      cardholderForm.cardholder_first_name.valid &&
      cardholderForm.cardholder_last_name.value?.trim() &&
      cardholderForm.cardholder_last_name.valid &&
      cardholderForm.cardholder_email.value?.trim() &&
      cardholderForm.cardholder_email.valid
    );
  };

  const isAddressValid = () => {
    if (cardholderForm.billing_address.value === 'same') {
      return true;
    }

    const address = cardholderForm.cardholder_addresslookup.value;
    return Boolean(
      address?.line_one?.trim() && address?.city?.trim() && address?.postal_code?.trim(),
    );
  };

  const billingAddressLabel = useMemo(() => {
    const address = cardholderForm.cardholder_addresslookup.value;
    if (
      isDifferentCardholderConfirmed &&
      cardholderForm.billing_address.value === 'other' &&
      address?.line_one?.trim()
    ) {
      const formattedAddress = `${address.line_one}${address.line_two ? ', ' + address.line_two : ''}, ${address.city}, ${address.postal_code}`;
      return `Use a different billing address: ${formattedAddress}`;
    }
    return 'Use a different billing address';
  }, [
    isDifferentCardholderConfirmed,
    cardholderForm.cardholder_addresslookup.value,
    cardholderForm.billing_address.value,
  ]);

  const billingAddressField = useMemo(() => {
    const field = { ...cardholderForm.billing_address };
    field.choices = [field.choices[0], ['other', billingAddressLabel]];
    return field;
  }, [cardholderForm.billing_address, billingAddressLabel]);

  useEffect(() => {
    if (cardholderForm.billing_address.value === 'other') {
      setIsDifferentCardholderConfirmed(false);
      setError('');
      setPaymentStatus(null);
      setPaymentToken(false);
    }
  }, [cardholderForm.billing_address.value, setError, setPaymentStatus, setPaymentToken]);

  useEffect(() => {
    setPaymentStatus(null);
    setPaymentToken(false);
    if (cardholderSelection === 'different') {
      setIsDifferentCardholderConfirmed(false);
    }
    setError('');
  }, [cardholderSelection, setError]);

  useEffect(() => {
    if (paymentStatus === 'successful') {
      setPaymentToken(true);
    } else {
      setPaymentToken(false);
    }
  }, [paymentStatus, setPaymentToken]);

  useEffect(() => {
    if (cardholderSelection === 'different' && isDifferentCardholderConfirmed) {
      setIsDifferentCardholderConfirmed(false);
      setPaymentStatus(null);
      setPaymentToken(false);
    }
  }, [
    cardholderForm.cardholder_first_name.value,
    cardholderForm.cardholder_last_name.value,
    cardholderForm.cardholder_email.value,
    cardholderForm.cardholder_addresslookup.value,
    cardholderSelection,
    setPaymentStatus,
    setPaymentToken,
  ]);

  return (
    <div className="cardholder">
      <form className="cardholder__radio-form">
        <fieldset className="cardholder__fieldset">
          <legend className="cardholder__header">Cardholder details *</legend>
          <p className="cardholder__subheader">Please select to confirm cardholder details.</p>
          <div className="cardholder__radio-options">
            <div className="cardholder__radio-option">
              <input
                type="radio"
                id="cardholder-same"
                name="whoIsTheCardholder"
                value="same"
                checked={cardholderSelection === 'same'}
                onChange={() => {
                  setCardholderSelection('same');
                  onChange('same');
                }}
              />
              <label htmlFor="cardholder-same">Same as patient details</label>
            </div>

            <div className="cardholder__radio-option">
              <input
                type="radio"
                id="cardholder-different"
                name="whoIsTheCardholder"
                value="different"
                checked={cardholderSelection === 'different'}
                onChange={() => {
                  setCardholderSelection('different');
                  onChange('different as patient details');
                }}
              />
              <label htmlFor="cardholder-different">Different to patient details</label>
            </div>
          </div>
        </fieldset>
      </form>

      {cardholderSelection === 'different' && (
        <div className="cardholder__form">
          <div className="cardholder__fields">
            <FormField
              field={cardholderForm.cardholder_first_name}
              errorText="Please re-enter the name without accented letters or spaces. For example, Verónica should be Veronica."
              errorEmpty="Please enter the cardholder first name."
            />
            <FormField
              field={cardholderForm.cardholder_last_name}
              errorText="Please re-enter the name without accented letters or spaces. For example, Pérez - Fernández should be Perez-Fernandez."
              errorEmpty="Please enter the cardholder last name."
            />
            <FormField
              field={cardholderForm.cardholder_email}
              errorText="Please use the following format: name@domain.com"
              errorEmpty="Please enter the cardholder email address."
            />
          </div>

          <div className="cardholder__billing-section">
            <FormField field={billingAddressField} direction="vertical" />

            {cardholderForm.billing_address.value === 'other' &&
              !isDifferentCardholderConfirmed && (
                <div className="cardholder__address-lookup">
                  <AddressLookup
                    setValue={cardholderForm.cardholder_addresslookup.setValue}
                    id="cardholder_addresslookup"
                    initialValue={cardholderForm.cardholder_addresslookup.value}
                    hideLabel={true}
                  />
                </div>
              )}

            {cardholderSelection === 'different' && !isDifferentCardholderConfirmed && (
              <CTAButton
                disabled={!isCardholderDetailsValid() || !isAddressValid()}
                action={{
                  call: () => setIsDifferentCardholderConfirmed(true),
                  name: 'Confirm cardholder details',
                  type: 'CONFIRM_CARDHOLDER_DETAILS',
                }}
              />
            )}

            {isDifferentCardholderConfirmed && cardholderForm.billing_address.value === 'other' && (
              <div className="cardholder__wrapper">
                <button
                  type="button"
                  className="clickableLinks"
                  onClick={() => {
                    setIsDifferentCardholderConfirmed(false);
                    setError('');
                  }}
                >
                  Edit address
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(cardholderSelection === 'same' ||
        (cardholderSelection === 'different' && isDifferentCardholderConfirmed)) &&
        paymentStatus !== 'successful' &&
        cardAttempts < 3 && (
          <>
            <div className="cardholder__iframe-wrapper">
              <div className="cardholder__iframe-header">
                <SafePayment />
                <span>Card details *</span>
              </div>
              <div id="cardholder__verifone-iframe" className="cardholder__verifone-iframe"></div>
            </div>
            <span style={{ visibility: !loading && 'hidden' }}>Loading...</span>
          </>
        )}
    </div>
  );
};
